import { Button } from "@/components/ui/button";
import { useState } from "react";
import { NeighborhoodFilter } from "./filters/NeighborhoodFilter";
import { EventTypeFilter } from "./filters/EventTypeFilter";
import { GroupSizeFilter } from "./filters/GroupSizeFilter";
import { PriceRangeFilter } from "./filters/PriceRangeFilter";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { FilterParams, FilterChangeHandler } from "@/types/filters";
import { RestaurantSearch } from "./RestaurantSearch";

export const EventFilters = ({ onFilterChange, onClearFilters }: FilterChangeHandler) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [filters, setFilters] = useState<FilterParams>({
    neighborhood: '',
    eventType: '',
    groupSize: '',
    priceRange: '',
    search: ''
  });

  const handleFilterChange = (type: keyof FilterParams, value: string) => {
    console.log('EventFilters - Filter changed:', { type, value });
    const newFilters = { ...filters, [type]: value };
    setFilters(newFilters);
    onFilterChange(type, value);
  };

  const handleClearFilters = () => {
    console.log('EventFilters - Clearing all filters');
    setFilters({
      neighborhood: '',
      eventType: '',
      groupSize: '',
      priceRange: '',
      search: ''
    });
    onClearFilters();
    
    toast({
      title: "Filters Cleared",
      description: "All filters have been reset",
      duration: 2000,
    });
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="w-full space-y-4">
      {isMobile && (
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between"
        >
          <span>Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}</span>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      )}
      
      {(isExpanded || !isMobile) && (
        <div className={`flex flex-wrap items-center gap-2 md:gap-4 ${isMobile ? 'flex-col w-full animate-in fade-in-50 duration-200' : ''}`}>
          <div className={`flex flex-wrap gap-4 ${isMobile ? 'w-full flex-col' : ''}`}>
            <NeighborhoodFilter
              value={filters.neighborhood}
              onChange={(value) => handleFilterChange("neighborhood", value)}
            />
            <EventTypeFilter
              value={filters.eventType}
              onChange={(value) => handleFilterChange("eventType", value)}
            />
            <GroupSizeFilter
              value={filters.groupSize}
              onChange={(value) => handleFilterChange("groupSize", value)}
            />
            <PriceRangeFilter
              value={filters.priceRange}
              onChange={(value) => handleFilterChange("priceRange", value)}
            />
          </div>
          
          {isMobile && (
            <div className="w-full mt-4 border-t pt-4 border-primary/10">
              <RestaurantSearch onSearch={(value) => handleFilterChange("search", value)} />
            </div>
          )}
          
          <Button 
            variant="outline"
            onClick={handleClearFilters}
            size="sm"
            className={`${isMobile ? 'w-full mt-4' : ''} ${activeFiltersCount === 0 ? 'opacity-50' : ''}`}
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
};